import { useFieldConfig } from 'context/fields';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useErrorBoundary } from 'react-error-boundary';
import { isNotEmpty } from 'shared/utils/functions';
import FormBase from './Base';
import ErrorBoundary from './ErrorBoundary';

/**
 * A dynamic form component that handles:
 * - Auto-generates form fields based on entity type and active division.
 * - Handles data transformations between client/server formats.
 * - Supports global and division-specific field configurations.
 * - Manages default values and field groupings.
 * - Manages form state, validation and error handling.
 * - Supports both creation and editing modes.
 *
 * @param {import('./types').FormProps} props - Component props
 * @returns {JSX.Element} Form component wrapped in ErrorBoundary
 *
 * @example
 * // Build an edit form for an Order under the active Division.
 * <Form
 *   id="qbenchCreateOrderForm"
 *   entity={order}
 *   entityType={EntityType.ORDER}
 *   onSubmit={console.log}
 * />
 */
export default function Form(props) {
  return (
    <ErrorBoundary>
      <FormLoader {...props} />
    </ErrorBoundary>
  );
}

function FormLoader({ children, ...props }) {
  const { showBoundary } = useErrorBoundary();
  const {
    entity,
    entityType,
    rawDefaultValues,
    useGlobal = false,
    showAllFields = false,
    fieldsToShow,
    fieldsToMerge,
  } = props;
  if (!entityType)
    showBoundary({
      message:
        'The `entityType` prop is required to create a form.',
    });

  const isEditing = !isEmpty(entity) || !isEmpty(rawDefaultValues);
  const isSelective = isNotEmpty(fieldsToShow);
  const { getFields, getDefaultValues, processEntity, isLoading } =
    useFieldConfig();
  const baseOpts = useMemo(
    () => ({
      useGlobal,
      entityType,
      fieldsToShow,
      fieldsToMerge,
    }),
    [useGlobal, entityType, fieldsToShow, fieldsToMerge]
  );
  const processedEntity = useMemo(
    () =>
      isEditing && !rawDefaultValues
        ? processEntity({ ...baseOpts, entity, mode: 'fromServer' })
        : entity,
    [isEditing, rawDefaultValues, processEntity, baseOpts, entity]
  );
  const defaultValues = useMemo(
    () =>
      rawDefaultValues || getDefaultValues({ ...baseOpts, entity: processedEntity }),
    [rawDefaultValues, getDefaultValues, baseOpts, processedEntity]
  );
  const fields = useMemo(
    () =>
      getFields({ ...baseOpts, showAllFields }),
    [getFields, baseOpts, showAllFields]
  );
  const hasNoFields = isEmpty(fields);

  if (isLoading)
    return (
      <div className="d-grid place-items-center w-100 h-100">
        <Spinner animation="border" variant="secondary" />
      </div>
    );

  return (
    <FormBase
      fields={fields}
      isSelective={isSelective}
      hasNoFields={hasNoFields}
      defaultValues={defaultValues}
      {...props}
    >
      {children}
    </FormBase>
  );
}
